"use client";

import { ArrowRight, ClipboardCheck, Compass, FileText, Layers, ShieldCheck, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  icon: ClipboardCheck,
  title: "Data Discovery",
  description: "Comprehensive assessment of your data infrastructure, pipelines, and reporting maturity to identify actionable insights."
}, {
  icon: Layers,
  title: "Metric Architecture",
  description: "Define standardized KPIs and data models that ensure consistency across your entire organizational reporting stack."
}, {
  icon: Sparkles,
  title: "Predictive Modeling",
  description: "Develop custom machine learning models to forecast trends, customer behavior, and operational performance metrics."
}, {
  icon: FileText,
  title: "Dashboard Design",
  description: "Create intuitive, high-impact visualization dashboards that translate complex datasets into clear business narratives."
}, {
  icon: Compass,
  title: "Strategy Roadmap",
  description: "Pragmatic implementation plans that align your data initiatives with long-term business growth and scalability goals."
}, {
  icon: ShieldCheck,
  title: "Governance & Compliance",
  description: "Establish robust data security, privacy protocols, and quality standards to ensure your analytics remain trustworthy."
}];
export default function Analytics() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="9653" id="our-data-analytics-capabilities" className="border-t border-border py-24 md:py-32 dark bg-accent">
      <div className="mx-auto w-full max-w-[1360px] px-6 md:px-10 lg:px-14">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 transition-all duration-[360ms] ease-[cubic-bezier(0.32,0.72,0,1)] ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="lg:col-span-7">
            <p className="font-default text-xs font-semibold uppercase tracking-[0.14em] text-foreground/70 mb-6" style={{
            fontFamily: "var(--font-mono)"
          }}>
              {"Our expertise"}
            </p>
            <h2 className="font-default font-black text-foreground text-[clamp(2.25rem,4.6vw,4.5rem)] leading-[0.96] tracking-[-0.035em]">
              Data analytics{" "}
              
            </h2>
          </div>

          <div className="lg:col-span-5 flex flex-col justify-end">
            <p className="font-default text-base md:text-lg text-foreground/80 leading-[1.55] tracking-[-0.005em] mb-6">Practical analytical services designed to transform complex information into clear, reliable, and useful business insight. Each solution is tailored to your reporting requirements, available data, and decision-making needs.</p>
            
          </div>
        </div>

        {}
        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, i) => {
          const Icon = service.icon;
          return <ServiceCard key={i} index={i} Icon={Icon} title={service.title} description={service.description} />;
        })}
        </div>
      </div>
    </section>;
}
function ServiceCard({
  index,
  Icon,
  title,
  description
}: {
  index: number;
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  description: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <div data-index={index} ref={ref} style={{
    transitionDelay: `${index * 60}ms`
  }} className={`group relative bg-card border border-border p-6 md:p-8 rounded-md flex flex-col transition-all duration-[360ms] ease-[cubic-bezier(0.32,0.72,0,1)] hover:-translate-x-[2px] hover:-translate-y-[2px] hover:border-foreground hover:border-[1.5px] hover:shadow-[4px_4px_0_0_var(--foreground)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
      <div className="mb-8">
        <Icon className={`h-6 w-6 ${index === 1 ? "text-white" : "text-foreground"}`} strokeWidth={1.5} />
      </div>

      <h3 className="font-default text-foreground text-xl md:text-[1.375rem] font-semibold tracking-[-0.01em] leading-[1.2] mb-4">
        {title}
      </h3>

      <p className="font-default text-sm text-muted-foreground leading-[1.6] mb-8 flex-1">
        {description}
      </p>

      <Link to="/OurServicesPage" className="inline-flex items-center gap-2 font-default text-sm font-semibold text-foreground self-start hover:text-primary transition-colors duration-[220ms]">
        
        
      </Link>
    </div>;
}